import { useDisclosure } from '@chakra-ui/react';
import {
  ApprovalStatusEnum,
  IApprovalRespSchema,
  IQuoteRespSchema,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  FunctionComponent as FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IoMdAlert } from 'react-icons/io';
import { MdCheck, MdClose, MdRemove } from 'react-icons/md';
import {
  approvalsQueryKeys,
  doQuoteApprove,
} from '../../../api/approvalService';
import { notesServiceQueryKeys } from '../../../api/queryKeysService';
import { APPROVAL } from '../../../constants';
import { QUOTE_SUBMITTED_STATUSES } from '../../../constants/quotes';
import { usePrevious } from '../../../hooks/usePrevious';
import { useAuth } from '../../../services/auth0';
import { useFlags } from '../../../services/launchDarkly';
import { pluralize } from '../../../utils';
import { sortQuoteApprovals } from '../../../utils/quotes';
import { MBox, MFlex, MIcon, MStack, MText, MVStack } from '../../Monetize';
import { QuoteProgressStep } from './QuoteProgressStep';
import { QuoteStepApprovalItem } from './QuoteStepApprovalItem';

export interface IApprovalGroup {
  ruleId: string;
  name: string;
  rank: number;
  approval: IApprovalRespSchema[];
}

interface QuoteStepApprovalProps {
  quote: IQuoteRespSchema;
  approvals: IApprovalRespSchema[];
  reloadData?: () => void;
  disableAutoOpenApprovalPopover?: boolean;
  isLoading?: boolean;
}

const isGroupAtActiveRank = (
  group: IApprovalGroup,
  activeRank: number | null,
): boolean => activeRank !== null && group.rank === activeRank;

const getGroupStyles = (
  groupIsApproved: boolean,
  groupIsDeclined: boolean,
  groupCanDoActions: boolean,
) => {
  if (groupIsApproved) {
    return {
      fontColor: 'tGreen.approval',
      textColor: 'tWhite.base',
      circleBackColor: 'tGreen.approval',
    };
  }
  if (groupIsDeclined) {
    return {
      fontColor: 'tRed.base',
      textColor: 'tRed.base',
      circleBackColor: 'tWhite.base',
    };
  }
  if (groupCanDoActions) {
    return {
      fontColor: 'tIndigo.base',
      textColor: 'tIndigo.base',
      circleBackColor: 'tWhite.base',
    };
  }
  return {
    fontColor: 'tGray.lightPurple',
    textColor: 'tGray.darkPurple',
    circleBackColor: 'tWhite.base',
  };
};

interface ApprovalGroupItemProps {
  group: IApprovalGroup;
  groupIndex: number;
  totalGroups: number;
  userId: string | undefined;
  isActionable: boolean;
  quote: IQuoteRespSchema;
  onClose: () => void;
  onApprove: (approval: IApprovalRespSchema) => void;
  isLoading?: boolean;
  reloadData?: () => void;
}

const ApprovalGroupItem: FC<ApprovalGroupItemProps> = ({
  group,
  groupIndex,
  totalGroups,
  userId,
  isActionable,
  quote,
  onClose,
  onApprove,
  isLoading,
  reloadData,
}) => {
  const groupIsApproved = group.approval.every(
    (a) => a.status === ApprovalStatusEnum.APPROVED || a.skipped,
  );
  const groupIsDeclined = group.approval.some(
    (a) => a.status === ApprovalStatusEnum.DECLINED,
  );
  const groupCanDoActions = group.approval.some(
    (a) => (userId === a.userId || a.currentUserApprover) && isActionable,
  );
  const showConnectionLine = groupIndex < totalGroups - 1;
  const styles = getGroupStyles(
    groupIsApproved,
    groupIsDeclined,
    groupCanDoActions,
  );

  return (
    <MFlex w="100%" pt="1">
      <MBox px="2" position="relative">
        <MBox
          w={5}
          h={5}
          borderRadius="50%"
          borderWidth="2px"
          borderColor={styles.fontColor}
          backgroundColor={styles.circleBackColor}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {groupIsDeclined ? (
            <MIcon as={MdClose} color={styles.textColor} />
          ) : groupIsApproved ? (
            <MIcon as={MdCheck} color={styles.textColor} />
          ) : groupCanDoActions ? (
            <MIcon as={MdRemove} color={styles.textColor} />
          ) : (
            <MText fontWeight="semibold" color={styles.textColor}>
              {groupIndex + 1}
            </MText>
          )}
        </MBox>
        {showConnectionLine && (
          <MBox
            height="100%"
            position="absolute"
            backgroundColor="tGray.lightPurple"
            width="1.5px"
            left="calc(50% - 0.75px)"
          />
        )}
      </MBox>
      <MStack width="80%">
        <MText fontSize="14px" fontWeight={400}>
          {group.name}
        </MText>
        <MBox flex="1" minW="0">
          {group.approval.map((approval, approvalIndex) => {
            const isCurrentUserOrTeam =
              userId === approval.userId || approval.currentUserApprover;
            const isPreviousApproverApproved =
              approvalIndex === 0 ||
              group.approval
                .slice(0, approvalIndex)
                .every(
                  (approver) =>
                    approver.status === ApprovalStatusEnum.APPROVED ||
                    approver.skipped,
                );
            return (
              <QuoteStepApprovalItem
                key={approval.id}
                approval={approval}
                quote={quote}
                canDoActions={isCurrentUserOrTeam && isActionable}
                isPreviousApproverApproved={isPreviousApproverApproved}
                onClose={onClose}
                onApprove={onApprove}
                isLoading={isLoading}
                reloadData={reloadData}
              />
            );
          })}
        </MBox>
      </MStack>
    </MFlex>
  );
};

const getCurrentIndex = (
  isQuoteSubmitted: boolean,
  internalApprovals: IApprovalGroup[],
) => {
  if (!isQuoteSubmitted) {
    return -1;
  }

  let i = 0;
  for (i = 0; i < internalApprovals.length; i += 1) {
    const hasNonApproved = internalApprovals[i].approval.some(
      (a) => !a.skipped && a.status !== APPROVAL.APPROVAL_STATUS.APPROVED,
    );
    if (hasNonApproved) {
      break;
    }
  }
  return i;
};

export const QuoteStepApproval: FC<QuoteStepApprovalProps> = React.forwardRef<
  any,
  QuoteStepApprovalProps
>(
  (
    {
      quote,
      approvals,
      reloadData,
      isLoading: isLoadingProp,
      disableAutoOpenApprovalPopover,
    },
    ref,
  ) => {
    let stepTitle = 'Get X approvals';
    let stepDate: string | null = null;
    const isQuoteSubmitted = QUOTE_SUBMITTED_STATUSES.has(quote.status);

    const groupedApprovals = useMemo((): IApprovalGroup[] => {
      const groupMap = new Map<string, IApprovalRespSchema[]>();
      for (const approval of approvals) {
        if (!groupMap.has(approval.ruleId)) {
          groupMap.set(approval.ruleId, []);
        }
        groupMap.get(approval.ruleId)?.push(approval);
      }
      const groups = Array.from(groupMap.entries()).map(([ruleId, items]) => ({
        ruleId,
        name: items[0].name ?? '',
        approval: [...items].sort((a, b) => {
          if (a.approverOrder === undefined || b.approverOrder === undefined) {
            return 0;
          }
          return a.approverOrder - b.approverOrder;
        }),
      }));
      return sortQuoteApprovals(
        groups.map((g) => ({
          ...g,
          rank: g.approval[0]?.rank ?? 0,
          createDate: g.approval[0]?.createDate ?? null,
        })),
      );
    }, [approvals]);

    const [internalApprovals, setInternalApprovals] = useState<
      IApprovalGroup[]
    >(() => groupedApprovals);
    const previousInternalApprovals = usePrevious(internalApprovals);
    const popoverState = useDisclosure();

    const { userId } = useAuth();
    const { enableRankOrderedApprovalGating } = useFlags();
    const [currentIndex, setCurrentIndex] = useState(() =>
      getCurrentIndex(isQuoteSubmitted, internalApprovals),
    );
    const [isLoading, setIsLoading] = useState(false);
    const [showHighlight, setShowHighlight] = useState(false);

    const queryClient = useQueryClient();

    const totalItemCount = internalApprovals.length;

    /**
     * TODO: remove skipped after https://monetizenow.atlassian.net/browse/BP-13088
     */
    const totalApprovalCount = Math.min(
      internalApprovals.filter((g) =>
        g.approval.every(
          (a) => a.status === APPROVAL.APPROVAL_STATUS.APPROVED || a.skipped,
        ),
      ).length,
      totalItemCount,
    );

    const isCompleted =
      !!quote.approvalSubmittedAt && currentIndex === internalApprovals.length;
    const isDisabled =
      quote.status === QuoteStatusEnum.DRAFT && internalApprovals.length === 0;
    const isCurrent =
      quote.status === QuoteStatusEnum.REVIEW ||
      quote.status === QuoteStatusEnum.DENIED;
    // Finds the minimum rank among all groups that are not yet fully approved.
    // - Start: min = null (no active rank found yet)
    // - For each group: if every approver is APPROVED or skipped → skip (group is done);
    //   otherwise take its rank and keep whichever is smaller: min vs this rank.
    // - Result: the lowest rank that still has pending approvals.
    //   Groups at this rank are actionable in parallel; higher-rank groups are locked.
    //
    // Example:
    //   Group A (rank 1) fully approved → skipped
    //   Group B (rank 2) pending        → rank 2 becomes candidate
    //   Group C (rank 2) pending        → min(2, 2) = 2
    //   Group D (rank 3) pending        → min(2, 3) = 2
    //   activeRank = 2 → B and C enabled (parallel), D disabled.
    //
    // When all groups are approved, min is never updated → activeRank = null → nothing actionable.
    const activeRank =
      enableRankOrderedApprovalGating && isQuoteSubmitted
        ? internalApprovals.reduce<number | null>((min, group) => {
            if (
              group.approval.every(
                (a) => a.status === ApprovalStatusEnum.APPROVED || a.skipped,
              )
            ) {
              return min;
            }
            return min === null ? group.rank : Math.min(min, group.rank);
          }, null)
        : null;

    const showInprogressStepNumber = isCurrent && !isCompleted;
    const showStepPopover = internalApprovals.length > 0 && !isDisabled;
    let isError = false;
    let showLightBackground = false;

    if (quote.status === QuoteStatusEnum.DENIED) {
      stepTitle = 'Approval Denied';
      isError = true;
      showLightBackground = true;
    } else if (totalItemCount === 0) {
      stepTitle = 'No Approvals Required';
    } else if (totalItemCount > 0 && totalApprovalCount > 0) {
      stepTitle = `${totalApprovalCount}/${totalItemCount} ${pluralize(
        'Approval',
        totalItemCount,
      )} Received`;
    } else if (totalItemCount > 0 && quote.status === QuoteStatusEnum.DRAFT) {
      stepTitle = `Get ${totalItemCount} ${pluralize(
        'Approval',
        totalItemCount,
      )}`;
    } else if (totalItemCount > 0 && quote.status !== QuoteStatusEnum.DRAFT) {
      stepTitle = `${totalApprovalCount}/${totalItemCount} ${pluralize(
        'Approval',
        totalItemCount,
      )} Received`;
    }

    if (isCompleted) {
      stepDate = quote.approvalGrantedAt;
    }

    useEffect(() => {
      setInternalApprovals(groupedApprovals);
    }, [groupedApprovals]);

    useEffect(() => {
      setCurrentIndex(getCurrentIndex(isQuoteSubmitted, internalApprovals));
    }, [internalApprovals, isQuoteSubmitted]);

    // When the approval amount changes, this field will briefly show a dropshadow background and blue stroke for a few seconds before going back to normal:
    useEffect(() => {
      if (!previousInternalApprovals) {
        return;
      }
      if (previousInternalApprovals.length !== totalItemCount) {
        setShowHighlight(true);
        setTimeout(() => {
          setShowHighlight(false);
        }, 1500);
      }
    }, [previousInternalApprovals, totalItemCount]);

    const onApprove = async (approval: IApprovalRespSchema) => {
      // this function and use of reloadData to be removed once all consumers of this component are on React Query
      setIsLoading(true);
      try {
        await doQuoteApprove(approval.id);
        queryClient.invalidateQueries({
          queryKey: [...approvalsQueryKeys.dashboard()],
        });
        queryClient.invalidateQueries({
          queryKey: [...notesServiceQueryKeys.noteListById('quotes', quote.id)],
        });
      } catch (err) {
        // pass
      }
      setIsLoading(false);

      reloadData && reloadData();
    };

    // True only when the current user has a pending approval in a group at the active rank.
    // Checks three conditions together:
    //   1. The group is at activeRank (lower-rank groups are still in progress, higher-rank are locked)
    //   2. The user is an approver in that group (matched by userId or currentUserApprover flag)
    //   3. Their specific approval is NO_ACTION and not skipped
    //
    // Example (activeRank = 1, logged-in user = legal):
    //   Group A (rank 1): legal has slot → status APPROVED      → condition 3 fails → false
    //   Group B (rank 1): legal has slot → skipped              → condition 3 fails → false
    //   Group C (rank 1): legal has no slot                     → condition 2 fails → false
    //   Group D (rank 3): legal has slot, NO_ACTION, not skipped → condition 1 fails (rank 3 ≠ 1) → false
    //   → currentUserNeedsAction = false → "Requires your approval" banner hidden.
    const currentUserNeedsAction = enableRankOrderedApprovalGating
      ? internalApprovals.some(
          (group) =>
            isGroupAtActiveRank(group, activeRank) &&
            group.approval.some(
              (a) =>
                (a.currentUserApprover || userId === a.userId) &&
                a.status === ApprovalStatusEnum.NO_ACTION &&
                !a.skipped,
            ),
        )
      : internalApprovals.some(
          (group, index) =>
            index >= currentIndex &&
            group.approval.some(
              (a) => a.currentUserApprover || userId === a.userId,
            ),
        );

    const requiresApproval = isCurrent && currentUserNeedsAction && !isError;

    const [showedPopover, setShowedPopover] = useState(false);
    useEffect(() => {
      if (
        !showedPopover &&
        internalApprovals.length > 0 &&
        requiresApproval &&
        !disableAutoOpenApprovalPopover
      ) {
        popoverState.onOpen();
        setShowedPopover(true);
      }
    }, [
      internalApprovals,
      showedPopover,
      requiresApproval,
      popoverState,
      disableAutoOpenApprovalPopover,
    ]);

    return (
      <QuoteProgressStep
        key="quote-progress-approvals"
        stepTitle={
          <MBox>
            <MText fontSize="sm" fontWeight="500" color="inherit">
              {stepTitle}
            </MText>
          </MBox>
        }
        stepDate={
          requiresApproval ? (
            <MFlex alignItems="center">
              <MIcon
                color="tOrange.base"
                as={IoMdAlert}
                mr="1"
                w="3"
                h="3"
                mt="-0.5"
              />
              <MText fontSize="xs" color="tOrange.base">
                Requires your approval
              </MText>
            </MFlex>
          ) : (
            stepDate
          )
        }
        stepNumber={2}
        isCompleted={isCompleted}
        showInprogressStepNumber={showInprogressStepNumber}
        isDisabled={isDisabled}
        isCurrent={isCurrent}
        showStepPopover={showStepPopover}
        isError={isError}
        showLightBackground={showLightBackground}
        showHighlight={showHighlight}
        popoverContent={({ onClose }) => (
          <MBox p="2">
            <MBox
              maxH="350px"
              className="custom-scroll-bar-v1"
              overflowY="auto"
              pr="2"
            >
              <MVStack gap={2}>
                {internalApprovals.map((group, groupIndex) => {
                  const isGroupActionable = enableRankOrderedApprovalGating
                    ? isCurrent && isGroupAtActiveRank(group, activeRank)
                    : isCurrent;
                  return (
                    <ApprovalGroupItem
                      key={group.ruleId}
                      group={group}
                      groupIndex={groupIndex}
                      totalGroups={internalApprovals.length}
                      userId={userId}
                      isActionable={isGroupActionable}
                      quote={quote}
                      onClose={onClose}
                      onApprove={onApprove}
                      isLoading={isLoading || isLoadingProp}
                      reloadData={reloadData}
                    />
                  );
                })}
              </MVStack>
            </MBox>
          </MBox>
        )}
        containerProps={{ ml: -8 }}
        textProps={
          // If draft and approvals exist, show in purple
          quote.status === QuoteStatusEnum.DRAFT && approvals.length > 0
            ? { color: 'tPurple.base' }
            : {}
        }
        popoverState={popoverState}
      />
    );
  },
);
